import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { format } from 'date-fns';
import { 
  X, Calendar, User, Building2, Phone, Mail, Users, 
  Flag, Bell, CheckCircle2, Loader2, AlertCircle
} from 'lucide-react';
import { AsyncSearchSelect } from '@/components/AsyncSearchSelect';

function CreateTaskFromEmailModal({ isOpen, onClose, email, onTaskCreated }) {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [users, setUsers] = useState([]);
  const [detectedCompany, setDetectedCompany] = useState(null);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    task_type: 'follow_up',
    status: 'not_started',
    priority: 'medium',
    due_date: '',
    due_time: '10:00',
    reminder: '1_hour',
    lead_id: '',
    lead_label: '',
    company_id: '',
    company_label: '',
    assigned_to: '',
    assigned_to_label: ''
  });

  const token = localStorage.getItem('token');

  useEffect(() => {
    if (isOpen && email) {
      // Pre-fill form from email
      setFormData(prev => ({
        ...prev,
        title: email.subject || 'Follow up on email',
        description: `From: ${email.from_name || email.from_email}\nSubject: ${email.subject}\n\n${email.body_preview || ''}`
      }));

      // Auto-detect company from email domain
      const fromEmail = email.from_email || '';
      if (fromEmail.includes('@')) {
        const domain = fromEmail.split('@')[1];
        axios.get(`${API}/companies?search=${domain}`, {
          headers: { Authorization: `Bearer ${token}` }
        }).then(res => {
          const list = Array.isArray(res.data) ? res.data : (res.data?.data || []);
          if (list.length > 0) {
            setDetectedCompany(list[0]);
            setFormData(prev => ({ ...prev, company_id: list[0].id, company_label: list[0].name }));
          }
        }).catch(console.error);
      }

      // Fetch only the users list (small set); leads/companies are on-demand via AsyncSearchSelect
      fetchRelatedData();
    }
  }, [isOpen, email, token]);

  const fetchRelatedData = async () => {
    try {
      const usersRes = await axios.get(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } });
      const list = Array.isArray(usersRes.data) ? usersRes.data : (usersRes.data?.users || usersRes.data?.data || []);
      setUsers(list);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  // Async fetchers for AsyncSearchSelect
  const fetchLeadOptions = async (q) => {
    try {
      const res = await axios.get(`${API}/leads`, { params: { search: q, page: 1, limit: 20 }, headers: { Authorization: `Bearer ${token}` } });
      const data = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      return data;
    } catch { return []; }
  };
  const fetchCompanyOptions = async (q) => {
    try {
      const res = await axios.get(`${API}/companies`, { params: { search: q, page: 1, limit: 20 }, headers: { Authorization: `Bearer ${token}` } });
      const data = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      return data;
    } catch { return []; }
  };
  const fetchUserOptions = async (q) => {
    const qLower = (q || '').toLowerCase();
    if (!qLower) return users.slice(0, 20);
    return users.filter(u =>
      (u.full_name || u.name || '').toLowerCase().includes(qLower)
      || (u.email || '').toLowerCase().includes(qLower)
      || (u.role || '').toLowerCase().includes(qLower)
    ).slice(0, 20);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      // Combine date and time
      let due_date = null;
      if (formData.due_date) {
        due_date = `${formData.due_date}T${formData.due_time}:00`;
      }

      const taskData = {
        title: formData.title,
        description: formData.description,
        task_type: formData.task_type,
        status: formData.status,
        priority: formData.priority,
        due_date: due_date,
        reminder: formData.reminder || null,
        lead_id: formData.lead_id || null,
        company_id: formData.company_id || null,
        assigned_to: formData.assigned_to || null
      };

      const response = await axios.post(
        `${API}/tasks/from-email?email_id=${email.id}`,
        taskData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setMessage({ type: 'success', text: 'Task created successfully!' });
      
      setTimeout(() => {
        onTaskCreated && onTaskCreated(response.data);
        onClose();
      }, 1000);
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to create task' 
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-xl max-h-[90vh] flex flex-col" data-testid="create-task-modal">
        {/* Header */}
        <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Create Task from Email</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400">Convert this email into an actionable task</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Message */}
        {message.text && (
          <div className={`mx-4 mt-4 p-3 rounded-lg flex items-center gap-2 ${
            message.type === 'success' ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400' : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400'
          }`}>
            {message.type === 'success' ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
            {message.text}
          </div>
        )}

        {/* Auto-detected Company Banner */}
        {detectedCompany && (
          <div className="mx-4 mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg flex items-center gap-2">
            <Building2 className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            <span className="text-sm text-blue-700 dark:text-blue-300">
              Auto-detected company: <strong>{detectedCompany.name}</strong>
            </span>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Task Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
              required
              data-testid="task-title"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
              data-testid="task-description"
            />
          </div>

          {/* Task Type and Status Row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Task Type</label>
              <select
                value={formData.task_type}
                onChange={(e) => setFormData({ ...formData, task_type: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                data-testid="task-type"
              >
                <option value="call">📞 Call</option>
                <option value="follow_up">🔄 Follow-up</option>
                <option value="meeting">👥 Meeting</option>
                <option value="email">📧 Email</option>
                <option value="other">📋 Other</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Status</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                data-testid="task-status"
              >
                <option value="not_started">Not Started</option>
                <option value="in_progress">In Progress</option>
                <option value="done">Done</option>
              </select>
            </div>
          </div>

          {/* Priority and Reminder Row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                <Flag className="w-3 h-3 inline mr-1" />
                Priority
              </label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                data-testid="task-priority"
              >
                <option value="low">🟢 Low</option>
                <option value="medium">🟡 Medium</option>
                <option value="high">🟠 High</option>
                <option value="urgent">🔴 Urgent</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                <Bell className="w-3 h-3 inline mr-1" />
                Reminder
              </label>
              <select
                value={formData.reminder}
                onChange={(e) => setFormData({ ...formData, reminder: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                data-testid="task-reminder"
              >
                <option value="">No reminder</option>
                <option value="30_minutes">30 minutes before</option>
                <option value="1_hour">1 hour before</option>
                <option value="2_hours">2 hours before</option>
                <option value="1_day">1 day before</option>
              </select>
            </div>
          </div>

          {/* Due Date and Time Row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                <Calendar className="w-3 h-3 inline mr-1" />
                Due Date
              </label>
              <input
                type="date"
                value={formData.due_date}
                onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                min={format(new Date(), 'yyyy-MM-dd')}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                data-testid="task-due-date"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Due Time</label>
              <input
                type="time"
                value={formData.due_time}
                onChange={(e) => setFormData({ ...formData, due_time: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                data-testid="task-due-time"
              />
            </div>
          </div>

          {/* Relations Row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                <User className="w-3 h-3 inline mr-1" />
                Link to Lead
              </label>
              <AsyncSearchSelect
                value={formData.lead_id}
                valueLabel={formData.lead_label}
                onSelect={(l) => {
                  if (!l) setFormData(p => ({ ...p, lead_id: '', lead_label: '' }));
                  else setFormData(p => ({ ...p, lead_id: l.id, lead_label: `${l.name}${l.organization ? ` (${l.organization})` : ''}` }));
                }}
                fetchOptions={fetchLeadOptions}
                placeholder="Search leads by name, email, company…"
                testId="task-lead-search"
                renderOption={(l) => (
                  <div className="flex flex-col">
                    <span className="font-medium text-slate-900 dark:text-white truncate">{l.name}</span>
                    <span className="text-xs text-slate-500 dark:text-slate-400 truncate">
                      {l.organization || ''}{l.organization && l.email ? ' • ' : ''}{l.email || ''}
                    </span>
                  </div>
                )}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                <Building2 className="w-3 h-3 inline mr-1" />
                Related Company
              </label>
              <AsyncSearchSelect
                value={formData.company_id}
                valueLabel={formData.company_label}
                onSelect={(c) => {
                  if (!c) setFormData(p => ({ ...p, company_id: '', company_label: '' }));
                  else setFormData(p => ({ ...p, company_id: c.id, company_label: c.name }));
                }}
                fetchOptions={fetchCompanyOptions}
                placeholder="Search companies…"
                testId="task-company-search"
                renderOption={(c) => (
                  <div className="flex flex-col">
                    <span className="font-medium text-slate-900 dark:text-white truncate">{c.name}</span>
                    {(c.industry || c.city) && (
                      <span className="text-xs text-slate-500 dark:text-slate-400 truncate">
                        {c.industry || ''}{c.industry && c.city ? ' • ' : ''}{c.city || ''}
                      </span>
                    )}
                  </div>
                )}
              />
            </div>
          </div>

          {/* Assigned To */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              <Users className="w-3 h-3 inline mr-1" />
              Assign to Team Member
            </label>
            <AsyncSearchSelect
              value={formData.assigned_to}
              valueLabel={formData.assigned_to_label}
              onSelect={(u) => {
                if (!u) setFormData(p => ({ ...p, assigned_to: '', assigned_to_label: '' }));
                else setFormData(p => ({ ...p, assigned_to: u.id, assigned_to_label: `${u.full_name || u.name || u.email}${u.role ? ` (${u.role})` : ''}` }));
              }}
              fetchOptions={fetchUserOptions}
              placeholder="Assign to myself, or search teammate…"
              testId="task-assigned-to-search"
              minChars={0}
              debounceMs={150}
              renderOption={(u) => (
                <div className="flex flex-col">
                  <span className="font-medium text-slate-900 dark:text-white truncate">{u.full_name || u.name || u.email}</span>
                  <span className="text-xs text-slate-500 dark:text-slate-400 truncate">
                    {u.email}{u.role ? ` · ${u.role}` : ''}
                  </span>
                </div>
              )}
            />
          </div>
        </form>

        {/* Footer */}
        <div className="p-4 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || !formData.title}
            className="px-4 py-2 bg-slate-900 dark:bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 rounded-lg hover:bg-slate-800 dark:hover:bg-slate-800 dark:hover:bg-white disabled:opacity-50 flex items-center gap-2 transition"
            data-testid="create-task-submit"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            Create Task
          </button>
        </div>
      </div>
    </div>
  );
}

export default CreateTaskFromEmailModal;
